import { ElementIconType } from "@/components/ui/icons";
import {
  selectIElementsSlice,
  type OnlyIElementsStore,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { selectIElement } from "@faro-lotv/app-component-toolbox";
import { FaroText, FaroTextVariant } from "@faro-lotv/flat-ui";
import {
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementGenericImgSheet,
  isIElementWithTypeAndHint,
  type IElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import {
  fetchProjectIElements,
  selectAncestor,
  selectChildrenDepthFirst,
} from "@faro-lotv/project-source";
import { createMutationSetLayerToAreaPose } from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import {
  ContextMenuAction,
  ContextMenuActionHandlerArgs,
  ContextMenuActionType,
} from "../action-types";

/** ContextMenuAction to mark a sheet as aligned with an identity pose */
export const MARK_SHEET_AS_ALIGNED_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.markSheetAsAligned,
  label: "Mark as aligned",
  icon: ElementIconType.AlignIcon,
  handler: markSheetAsAligned,
};

/**
 * Mark a sheet as aligned by setting its pose to identity (no transformation relative to its parent area).
 */
async function markSheetAsAligned({
  elementID,
  apiClients,
  createDialog,
  dispatch,
  state,
}: ContextMenuActionHandlerArgs): Promise<void> {
  const hasConfirmed = await createDialog({
    title: "Mark sheet as aligned",
    confirmText: "Mark as aligned",
    content: (
      <FaroText variant={FaroTextVariant.bodyM}>
        The sheet will be marked as aligned in its current position. This allows
        it to be used as a reference for aligning other sheets.
      </FaroText>
    ),
  });

  if (!hasConfirmed) return;

  const alignMutation = createMutationSetLayerToAreaPose(
    elementID,
    1,
    { x: 0, y: 0, z: 0, w: 1 },
    { x: 0, y: 0, z: 0 },
  );

  await apiClients.projectApiClient.applyMutations([alignMutation]);

  const sheet = selectIElement(elementID)(state);
  const area = selectAncestor(sheet, isIElementAreaSection)(state);
  await dispatch(
    fetchProjectIElements({
      fetcher: async () =>
        await apiClients.projectApiClient.getAllIElements({
          ancestorIds: [area?.id ?? elementID],
        }),
    }),
  );
}

/**
 * @returns Selector to determine whether the mark as aligned action should be available.
 * The main purpose of this action is to allow a user to recover from a state, where no sheets in the project are aligned,
 * but it shouldn't be a default workflow.
 * @param state app state
 * @param sheet the sheet element to check
 */
export const selectShouldShowMarkAsAlignedAction = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, sheet: IElementGenericImgSheet) => sheet,
    ],
    (iElements, sheet): boolean => {
      const parent = selectIElement(sheet.parentId)({ iElements });

      // The action should only be available when the sheet is in an area, but there are more than one sheet.
      if (
        !parent ||
        !isIElementWithTypeAndHint(
          parent,
          IElementType.group,
          IElementTypeHint.area,
        )
      ) {
        return false;
      }

      const sheets = selectChildrenDepthFirst(
        parent,
        isIElementGenericImgSheet,
      )({ iElements });

      // An area with a single sheet is aligned by aligning the area itself, so the
      // sheet is already treated as aligned and this action must not be offered.
      // Otherwise, only offer it while none of the sheets is aligned yet.
      return sheets.length > 1 && !sheets.some(isSheetAligned);
    },
  ),
);

/**
 * @returns Whether the sheet is aligned by looking at whether it has a manually set pose.
 * @param iElement The sheet element to check.
 */
export function isSheetAligned(iElement: IElementGenericImgSheet): boolean {
  return !!iElement.pose && !!iElement.pose.pos && !!iElement.pose.scale;
}
